import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";
import React, { useState, useEffect } from "react";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import About from "./pages/About";
import Contact from "./pages/Contact";
import CheckoutPage from "./pages/Checkoutpage";
// import CheckoutPhonePe from "./pages/CheckoutPhonePe";
import CategoryProductPage from "./pages/CategoryProductPage";
// import CategoriesPage from "./pages/CategoryPage";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollTop";

import AdminAuthPage from "./pages/AdminAuthPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminProductPage from "./pages/AdminProductPage";
import AdminCategoryPage from "./pages/AdminCategoryPage";
import AdminOrders from "./pages/AdminOrder";
import GiftAdminPage from "./pages/GiftAdminPage";

import Navbar from "./components/Navbar";
import AdminLayout from "./components/AdminLayout";
import OrdersPage from "./pages/OrderPage";
import ChatWidget from "./components/ChatWidget";
import GiftingLanding from "./pages/GiftLandingPage";
import GiftList from "./pages/GiftList";
import GiftDetail from "./pages/GiftDetail";
import ReturnPolicyPage from "./pages/ReturnPolicy";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsConditionsPage from "./pages/TermsConditionsPage";
import ShippingPolicy from "./pages/ShippingPolicy";
import AdminContacts from "./pages/AdminContacts";
import FAQ from "./pages/FAQ";
import PriceSlabs from "./pages/PriceSlabs";
import { CartProvider } from "./CartContext";
import { ToastContainer } from "react-toastify";
import AdminInsights from "./pages/AdminInsights";
import WebsiteHealth from "./pages/WebsiteHealth";
import BlogList from "./pages/BlogList";
import BlogDetail from "./pages/BlogDetail";
import AdminBlogPage from "./pages/AdminBlogPage";
import FirstOrderCombo from "./pages/FirstOrderCombo";
import NewYearCombo419 from "./pages/NewYearOffer";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

// ProtectedRoute component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  return token && isAdmin ? children : <Navigate to="/admin/login" replace />;
};

// UserLayout with conditional Navbar
const UserLayout = ({ user, setUser }) => {
  const location = useLocation();
  // Hide Navbar on admin pages
  const isAdminRoute = location.pathname.startsWith("/admin");
  return (
    <>
      {!isAdminRoute && <Navbar user={user} setUser={setUser} />}
      <main className="pt-4">
        <Outlet />
      </main>
    </>
  );
};

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    setUser(storedUser || null);
  }, []);

  // Custom hook to get location for conditional rendering
  function useCurrentLocation() {
    const [location, setLocation] = useState(window.location.pathname);
    useEffect(() => {
      const updateLocation = () => setLocation(window.location.pathname);
      window.addEventListener("popstate", updateLocation);
      return () => window.removeEventListener("popstate", updateLocation);
    }, []);
    return location;
  }

  const location = useCurrentLocation();
  const isAdminRoute = location.startsWith("/admin");

  return (
    <BrowserRouter>
      <CartProvider>
        <ScrollToTop />
        {/* Hide Navbar on admin pages */}
        {!isAdminRoute && <Navbar user={user} setUser={setUser} />}
        <Routes>
          {/* Public / User Pages */}
          <Route element={<Outlet />}>
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Home />} />
            <Route path="/login" element={<Login setUser={setUser} />} />
            <Route path="/register" element={<Register setUser={setUser} />} />
            <Route path="/products" element={<Products />} />
              <Route path="/products/:id" element={<ProductDetail />} /> {/* ✅ */}
            
            <Route path="/cart" element={<Cart />} />
            <Route path="/aboutus" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            {/* <Route path="/checkout/phonepe" element={<CheckoutPhonePe />} /> */}
            <Route path="/refund-policy" element={<ReturnPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsConditionsPage />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            <Route path="/faqs" element={<FAQ />} />
            {/* <Route path="/categories" element={<CategoriesPage />} /> */}
            <Route path="/category/:slug" element={<CategoryProductPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/chat" element={<ChatWidget />} />
            <Route path="/gifthampers" element={<GiftingLanding />} />
            <Route path="/gifts" element={<GiftList />} />
            {/* <Route path="/gifts/:id" element={<ProductDetail />} /> */}
            <Route path="/gifts/all" element={<GiftList />} />
            <Route path="/gifts/:slug" element={<GiftDetail />} />
            <Route path="/blogs" element={<BlogList />} />
            <Route path="/blogs/:slug" element={<BlogDetail />} />
            <Route path="/first-order-combo" element={<FirstOrderCombo />} />
            <Route path="/new-year-combo-419" element={<NewYearCombo419 />} />
          </Route>

          {/* Admin Auth Pages */}
          <Route path="/admin/login" element={<AdminAuthPage />} />
          <Route path="/admin/logout" element={<AdminAuthPage />} />

          {/* Protected Admin Pages with Sidebar */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="products" element={<AdminProductPage />} />
            <Route path="categories" element={<AdminCategoryPage />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="gifts" element={<GiftAdminPage />} />
            <Route path="insights" element={<AdminInsights />} />
            <Route path="health" element={<WebsiteHealth />} />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="blogs" element={<AdminBlogPage />} />  
            <Route path="price-slabs" element={<PriceSlabs />} />

          </Route>
        </Routes>
        {/* Hide Footer on admin pages */}
        {/* Stick ChatWidget to right side on all non-admin pages */}
        {!isAdminRoute && <ChatWidget />}
        {!isAdminRoute && <Footer />}
        <ToastContainer position="top-right" autoClose={2000} />
      </CartProvider>
    </BrowserRouter>
  );
}

export default App;
